"use client";

import { useCallback, useEffect, useRef } from "react";

import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { VERSION_EVENTS, VERSION_REFRESH_COPY } from "@/lib/versioning";

const POLL_INTERVAL_MS = 60_000;

const getClientBuildId = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return (window as any)?.__NEXT_DATA__?.buildId ?? null;
};

const fetchLatestBuildId = async () => {
  try {
    const response = await fetch("/api/build-info", { cache: "no-store" });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as { buildId?: string | null };
    return payload.buildId ?? null;
  } catch (error) {
    console.warn("[VersionGuard] Failed to poll build info", error);
    return null;
  }
};

export function VersionGuard() {
  const { toast } = useToast();
  const hasPromptedRef = useRef(false);
  const isReloadingRef = useRef(false);

  const requestRefresh = useCallback(() => {
    if (isReloadingRef.current) {
      return;
    }
    isReloadingRef.current = true;
    window.dispatchEvent(new Event(VERSION_EVENTS.BEFORE_REFRESH));
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }, []);

  const showRefreshToast = useCallback(() => {
    if (hasPromptedRef.current) {
      return;
    }
    hasPromptedRef.current = true;
    toast({
      title: VERSION_REFRESH_COPY.title,
      description: VERSION_REFRESH_COPY.description,
      duration: 60_000,
      action: (
        <ToastAction altText="Refresh" onClick={requestRefresh}>
          Refresh
        </ToastAction>
      ),
    });
  }, [requestRefresh, toast]);

  useEffect(() => {
    const handleStaleEvent = () => {
      showRefreshToast();
    };

    window.addEventListener(VERSION_EVENTS.VERSION_STALE, handleStaleEvent);
    window.addEventListener(VERSION_EVENTS.SERVER_ACTION_MISSING, handleStaleEvent);

    return () => {
      window.removeEventListener(VERSION_EVENTS.VERSION_STALE, handleStaleEvent);
      window.removeEventListener(VERSION_EVENTS.SERVER_ACTION_MISSING, handleStaleEvent);
    };
  }, [showRefreshToast]);

  useEffect(() => {
    const clientBuildId = getClientBuildId();
    if (!clientBuildId) {
      return;
    }

    let cancelled = false;

    const maybeMarkStale = async () => {
      const latestBuild = await fetchLatestBuildId();
      if (!latestBuild || cancelled) {
        return;
      }
      if (latestBuild !== clientBuildId) {
        window.dispatchEvent(new Event(VERSION_EVENTS.VERSION_STALE));
        showRefreshToast();
      }
    };

    // Initial ping plus polling cadence
    void maybeMarkStale();
    const intervalId = window.setInterval(() => {
      void maybeMarkStale();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [showRefreshToast]);

  return null;
}
